import { forwardRef, HTMLAttributes, HTMLProps, PropsWithChildren, ReactNode } from "react";
import cn from "clsx";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { ClipboardCopyButton } from "../ClipboardCopy";

export interface GridProps extends PropsWithChildren {
  columns?: number;
  className?: string;
}

function Grid({ children, columns = 2, className, ...rest }: GridProps) {
  return (
    <div
      className={cn("grid gap-x-2 gap-y-4", className, {
        "grid-cols-1": columns === 1,
        "grid-cols-2": columns === 2,
        "grid-cols-3": columns === 3,
      })}
      {...rest}
    >
      {children}
    </div>
  );
}

export const CellVariants = ["default", "ghost", "light"];
type CellVariant = (typeof CellVariants)[number];

interface CellProps extends Omit<HTMLProps<HTMLDivElement>, "title"> {
  variant?: CellVariant;
  title?: ReactNode;
  titleTail?: ReactNode;
  tooltip?: string | ReactNode;
  copyString?: string;
  "data-testid"?: string;
  /** When set, the title div gets this id and the value div gets aria-labelledby, so the value is findable by getByLabelText(title). */
  id?: string;
  onClick?: () => void;
}

function Cell({
  variant = "default",
  title,
  titleTail,
  children,
  copyString,
  tooltip,
  className,
  "data-testid": dataTestId,
  id: cellId,
  onClick,
}: CellProps) {
  return (
    <div
      className={cn(
        "relative group px-2 py-2 rounded-xl",
        variant === "default" && "bg-background-surface-neutral",
        variant === "light" && "bg-background-surface",
        variant !== "ghost" && "overflow-hidden rounded-sm border border-border-action",
        className
      )}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {title && (
        <div className="flex items-center justify-between gap-1 text-left">
          <div className="flex gap-1">
            <div className="text-xs text-text-base-secondary-a" id={cellId}>
              {title}
            </div>
            {titleTail}
          </div>

          {tooltip && (
            <Tooltip.Wrapper>
              <Tooltip.Trigger>
                <i className="icon-lt-information-line print:hidden" />
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content size="sm" side="bottom" align="end" theme={Theme.Light}>
                  {typeof tooltip === "string" ? <Tooltip.Description>{tooltip}</Tooltip.Description> : tooltip}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Wrapper>
          )}
        </div>
      )}
      <div
        className={cn("text-left w-full text-sm font-medium", {
          "pr-10": !!copyString,
        })}
        aria-labelledby={cellId ?? undefined}
      >
        {children}
      </div>
      {copyString && (
        <div className="absolute hidden -translate-y-1/2 group-hover:block right-2 top-1/2">
          <ClipboardCopyButton text={copyString} roundedLeft roundedRight />
        </div>
      )}
    </div>
  );
}

function Badge({ children }: PropsWithChildren) {
  return <div className="px-1 text-xs bg-blue-grey-200 text-blue-grey-950/50 rounded-xs">{children}</div>;
}

const Button = forwardRef<HTMLButtonElement, HTMLAttributes<HTMLButtonElement>>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "w-full text-left cursor-pointer text-platform-blue-600 hover:text-platform-blue-700 active:text-platform-blue-800",
      className
    )}
    {...props}
  />
));

Button.displayName = "DataPanel.Button";

export const DataPanel = { Grid, Cell, Button, Tails: { Badge } };
